import { IntlShape } from 'react-intl'
import { differenceInDays, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

const MS_PER_DAY = 24 * 60 * 60 * 1000

// calendar day number in the given timezone, so day comparisons ignore DST-length days
const getZonedDayNumber = (date: Date, timeZone: string): number => {
  const [year, month, day] = formatInTimeZone(date, timeZone, 'yyyy-MM-dd').split('-').map(Number)
  return Date.UTC(year, month - 1, day) / MS_PER_DAY
}

const isSupportedTimeZone = (timeZone?: string): timeZone is string => {
  if (!timeZone) {
    return false
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone })
    return true
  } catch {
    return false
  }
}

// friendly format the time stamp
// rawTimeStamp is expected to be ISO 8601 or epoch ms; parseISO returns Invalid Date for other string formats that moment used to accept
// make sure to always keep timestamp even though we don't display it
export const getFriendlyFormattedTimeStamp = (intl: IntlShape, rawTimeStamp: string | number): string => {
  if (rawTimeStamp) {
    // intl.timeZone will not be undefined unless user has not logged in or has not done search for care at least once.
    // In both those cases we do not show any dates/times and hence this function is not called.
    // An unsupported zone would make formatInTimeZone throw during render, so fall back to the device zone.
    const displayedTimezone = isSupportedTimeZone(intl.timeZone)
      ? intl.timeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone
    const now = new Date()
    // message date_created can arrive as epoch ms, which parseISO throws on
    const timeStamp = typeof rawTimeStamp === 'number' ? new Date(rawTimeStamp) : parseISO(rawTimeStamp)
    const isValidTimeStamp = !isNaN(timeStamp.getTime())

    const daysFromToday = isValidTimeStamp
      ? getZonedDayNumber(timeStamp, displayedTimezone) - getZonedDayNumber(now, displayedTimezone)
      : NaN

    const isSameDay = daysFromToday === 0
    const isYesterday = daysFromToday === -1
    const isTomorrow = daysFromToday === 1

    const elapsedDays = isValidTimeStamp ? differenceInDays(now, timeStamp) : NaN
    const isWithinAWeek = elapsedDays >= 1 && elapsedDays <= 7

    // if the last message was sent today show as 1:00 pm format
    if (isSameDay) {
      return intl.formatTime(rawTimeStamp)
      // if last message was sent with 7 days
    } else if (isYesterday) {
      return intl.formatMessage({
        defaultMessage: 'Yesterday',
        description: 'The day before today.',
      })
    } else if (isTomorrow) {
      return intl.formatMessage({
        defaultMessage: 'Tomorrow',
        description: 'The day after today.',
      })
    } else if (isWithinAWeek) {
      return intl.formatDate(rawTimeStamp, { weekday: 'long' })
    } else {
      return intl.formatDate(rawTimeStamp, { day: '2-digit', year: 'numeric', month: '2-digit' })
    }
  }
  return ''
}
